import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, RefreshCw, Image as ImageIcon, FileText } from "lucide-react";

const MAX_FILES = 4;
const MAX_SIZE = 10 * 1024 * 1024;
const ALLOWED = new Set([
  "image/jpeg", "image/png", "image/webp",
  "image/heic", "image/heif", "application/pdf",
]);
const ALLOWED_EXTS = ["jpg", "jpeg", "png", "webp", "heic", "heif", "pdf"];

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Compact attachment picker for the composer.
 * - Small "Priloži fajl" button (no big drop zone on mobile).
 * - Immediate preview cards per file with name, size, status, remove, retry.
 * - Reports state upstream via onChange so composer can block submit while
 *   any file is still uploading or in error.
 *
 * value: array of items { key, file, id?, mime_type?, size, status: 'uploading'|'ready'|'error', error? }
 */
export default function AttachmentPicker({ value = [], onChange, disabled = false }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const updateItem = (key, patch) => {
    onChange((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };
  const removeItem = (item) => {
    onChange((prev) => prev.filter((it) => it.key !== item.key));
    if (item.id) {
      api.delete(`/files/${item.id}`).catch(() => {});
    }
  };

  const uploadOne = async (item) => {
    updateItem(item.key, { status: "uploading", error: null });
    const form = new FormData();
    form.append("file", item.file);
    try {
      const { data } = await api.post("/uploads/comment-attachment", form);
      updateItem(item.key, {
        status: "ready",
        id: data.id,
        mime_type: data.mime_type,
        size: data.size,
      });
    } catch (e) {
      updateItem(item.key, { status: "error", error: formatApiError(e) });
    }
  };

  const acceptFiles = (list) => {
    if (disabled) return;
    const files = Array.from(list || []);
    if (!files.length) return;
    const remaining = MAX_FILES - value.length;
    if (remaining <= 0) {
      toast.error(`Maksimalno ${MAX_FILES} fajla po upisu`);
      return;
    }
    const picked = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Dodato prvih ${remaining} fajl(ova), ostatak preskočen`);
    }
    const nextItems = [];
    for (const f of picked) {
      const ext = (f.name || "").split(".").pop().toLowerCase();
      const okMime = ALLOWED.has(f.type) || ALLOWED_EXTS.includes(ext);
      if (!okMime) {
        toast.error(`„${f.name}" — format nije podržan`);
        continue;
      }
      if (f.size > MAX_SIZE) {
        toast.error(`„${f.name}" je prevelik (maks 10 MB)`);
        continue;
      }
      const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const previewUrl = f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
      nextItems.push({
        key, file: f, id: null,
        original_filename: f.name,
        mime_type: f.type || "application/octet-stream",
        size: f.size,
        previewUrl,
        status: "uploading",
        error: null,
      });
    }
    if (nextItems.length) {
      onChange((prev) => [...prev, ...nextItems]);
      nextItems.forEach((it) => uploadOne(it));
    }
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div className="space-y-1.5" data-testid="attachment-picker">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          acceptFiles(e.dataTransfer?.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            acceptFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="attachment-input"
        />
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || value.length >= MAX_FILES}
          data-testid="attachment-open-btn"
          className={
            "inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border transition-colors " +
            (dragOver
              ? "border-forest bg-forest/8 text-forest"
              : "border-line bg-white text-ink-muted hover:border-ink/30 hover:text-ink") +
            ((disabled || value.length >= MAX_FILES) ? " opacity-50 cursor-not-allowed" : "")
          }
        >
          <Paperclip className="w-3.5 h-3.5" />
          Priloži fajl
          {value.length > 0 && (
            <span className="ml-1 text-forest/80 font-medium">
              {value.length}/{MAX_FILES}
            </span>
          )}
        </button>
      </div>

      {value.length > 0 && (
        <div className="space-y-1.5">
          {value.map((it) => {
            const isImage = (it.mime_type || "").startsWith("image/");
            const isHEIC = (it.mime_type || "").includes("heic") || (it.mime_type || "").includes("heif");
            const showThumb = isImage && !isHEIC && it.previewUrl;
            return (
              <div
                key={it.key}
                data-testid={`attachment-item-${it.key}`}
                className={
                  "flex items-center gap-2 border rounded-md pl-2 pr-1 py-1.5 text-xs " +
                  (it.status === "error"
                    ? "border-terracotta/40 bg-terracotta/6"
                    : "border-line bg-white")
                }
              >
                {showThumb ? (
                  <img
                    src={it.previewUrl}
                    alt=""
                    className="w-9 h-9 rounded object-cover flex-shrink-0"
                  />
                ) : isImage ? (
                  <div className="w-9 h-9 rounded flex-shrink-0 flex items-center justify-center bg-ochre/10 border border-ochre/30" title="HEIC — preview nedostupan">
                    <ImageIcon className="w-4 h-4 text-ochre-ink" />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded flex-shrink-0 flex items-center justify-center bg-terracotta/10 border border-terracotta/30">
                    <FileText className="w-4 h-4 text-terracotta" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-ink" title={it.original_filename}>
                    {it.original_filename}
                  </div>
                  <div className="flex items-center gap-2 text-ink-muted text-[11px]">
                    <span>{fmtSize(it.size)}</span>
                    {it.status === "uploading" && (
                      <span className="inline-flex items-center gap-1 text-forest">
                        <span className="w-2.5 h-2.5 border border-forest/40 border-t-forest rounded-full animate-spin" />
                        Šaljem…
                      </span>
                    )}
                    {it.status === "ready" && (
                      <span className="text-forest">✓ Spremno</span>
                    )}
                    {it.status === "error" && (
                      <span className="text-terracotta" data-testid={`attachment-error-${it.key}`}>
                        {it.error || "Greška"}
                      </span>
                    )}
                  </div>
                </div>
                {it.status === "error" && (
                  <button
                    type="button"
                    onClick={() => uploadOne(it)}
                    className="text-terracotta hover:bg-terracotta/10 rounded p-1"
                    data-testid={`attachment-retry-${it.key}`}
                    title="Pokušaj ponovo"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeItem(it)}
                  className="text-ink-muted hover:text-terracotta rounded p-1"
                  data-testid={`attachment-remove-${it.key}`}
                  title="Ukloni"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
